import React, { Component, useMemo } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Loader } from "lucide-react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Select from "react-select";

import LangSelector from "components/utils/LangSelector";

import { validateAdminPayement } from "utils/data/Login";
import NotifMessage from "components/common/tools/NotifMessage";
import { getElementByUuid } from "utils/data/Content";

import { useTheme } from "@/src/contexts/ThemeContext";
import { THEME_TOKENS } from "@/src/theme/themeTokens";

import "css/admin/signup.scss";

function buildStripeElementOptions({ foreground, mutedForeground }) {
  return {
    style: {
      base: { color: foreground, "::placeholder": { color: mutedForeground } },
    },
  };
}

const STRIPE_ELEMENT_OPTIONS = {
  light: buildStripeElementOptions(THEME_TOKENS.light),
  dark: buildStripeElementOptions(THEME_TOKENS.dark),
};

export class SignupStep3UI extends Component {
  constructor(props) {
    super(props);

    this.state = {
      full_name: "",
      address: "",
      city: "",
      zip_code: "",
      country: {},
      cardholder_name: "",
      country_vat: {
        value: "fr",
        percentage: 20,
        label: "France",
      },
      vat_number: "",
      isWorld: false,
      validation: {
        isValid: true,
        error: "",
        code: "",
      },
      showValidation: false,
      loading: false,
    };
  }

  onChange = (target) => {
    this.setState({
      [target.id]: target.value,
    });
  };

  getCountryOptions = () => {
    const { countries, t } = this.props;
    const labels = t("languages:countries", { returnObjects: true });

    let objectCountries = [];

    if (!countries) return objectCountries;

    countries.data.forEach((country) => {
      if (labels[country.country]) {
        objectCountries.push({
          value: country.uuid,
          code: country.country,
          label: labels[country.country],
        });
      }
    });

    objectCountries = this.sortByAlpha(objectCountries);

    return objectCountries;
  };

  onSelectCountry = (selection) => {
    this.setState({
      country: selection,
    });
  };

  onPayment = () => {
    if (!this.state.loading) {
      const { elements, stripe } = this.props;

      if (!stripe || !elements) return;

      this.setState({
        showValidation: true,
      });

      const validation = validateAdminPayement(this.state);
      const address = {
        line1: this.state.address || "",
        city: this.state.city || "",
        postal_code: this.state.zip_code || "",
        country: this.state.country.code || "",
      };

      this.setState({ validation });

      if (validation.isValid) {
        const cardElement = elements.getElement(CardNumberElement);

        if (!cardElement) return;

        this.setState({
          loading: true,
        });

        stripe
          .createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: this.state.cardholder_name,
              address,
            },
          })
          .then((result) => {
            if (result.error) {
              this.setState({
                validation: {
                  isValid: false,
                  error: result.error.message,
                },
                loading: false,
              });
            } else {
              this.props.onPayment(
                result.paymentMethod.id,
                this.state.vat_number,
                this.state.country_vat,
                address,
                this.onConfirmPayment,
                this.onPaymentError,
              );
            }
          })
          .catch((error) => {
            this.setState({
              validation: {
                isValid: false,
                error: error.message,
              },
              loading: false,
            });
          });
      }
    }
  };

  onPaymentError = (e) => {
    if (e && e.details) {
      this.setState({
        loading: false,
        validation: {
          isValid: false,
          error: e.details.error.code,
        },
      });
    }
  };

  onConfirmPayment = (json) => {
    const {
      client_secret,
      sub_stripe_status,
      payment_intent_id,
      payment_intent_status,
    } = json;

    if (sub_stripe_status === "incomplete") {
      if (payment_intent_status === "requires_action") {
        this.props.stripe
          .confirmCardPayment(client_secret)
          .then((result) => {
            if (result.error) {
              this.setState({
                validation: {
                  isValid: false,
                  error: result.error.message,
                },
                loading: false,
              });
            } else {
              this.props.onConfirmPayment(
                result.paymentIntent.id,
                this.onPaymentError,
              );
            }
          })
          .catch((error) => {
            this.setState({
              validation: {
                isValid: false,
                error: error.message,
              },
              loading: false,
            });
          });
      }
    } else {
      this.props.onConfirmPayment(payment_intent_id, this.onPaymentError);
    }
  };

  getCountryRatesOptions = () => {
    const { t } = this.props;
    const objectCountries = [
      {
        value: "fr",
        percentage: 20,
        label: t("languages:countries.FR"),
      },
      {
        value: "euro",
        percentage: 20,
        label: t("languages:countries.EU"),
      },
      {
        value: "world",
        percentage: 0,
        label: t("languages:countries.ZZ"),
      },
    ];

    return objectCountries;
  };

  sortByAlpha = (countries) =>
    countries.sort((a, b) => {
      const x = a.label.toLowerCase();
      const y = b.label.toLowerCase();
      return x.localeCompare(y);
    });

  onChangeTax = (target) => {
    this.setState({
      country_vat: target,
      isWorld: target.value === "world",
    });
  };

  getTaxAmount = (price, tax_percentage) => (price * tax_percentage) / 100;

  getPlanSummary = () => {
    const { t, selected_plan, active_options, plans, plan_options } =
      this.props;
    const { country_vat, vat_number } = this.state;
    const elements = [];
    const planObj = getElementByUuid(selected_plan, plans);
    let first_price = 0;
    let next_price = 0;
    let first_tax_amount = 0;
    let next_tax_amount = 0;
    const percentage =
      vat_number && country_vat.value !== "fr" ? 0 : country_vat.percentage;

    if (planObj) {
      first_price = next_price = planObj.selected_option.price;
      elements.push(
        <div className="form-row" key="summary-plan">
          <div className="signup-sum-item">
            <div className="signup-sum-item-label">
              {planObj.selected_option.label}
            </div>
            <div className="signup-sum-item-price">
              {planObj.selected_option.price.toFixed(2)} €
            </div>
          </div>
        </div>,
      );
    }

    for (const i in active_options) {
      if (active_options[i]) {
        const option = getElementByUuid(i, plan_options);
        first_price += option.price;
        if (option.recurrency === 1) next_price += option.price;

        elements.push(
          <div className="form-row" key={`summary-opt-${i}`}>
            <div className="signup-sum-item">
              <div className="signup-sum-item-label">
                {t(`${option.type}_short`)}
              </div>
              <div className="signup-sum-item-price">
                {option.price.toFixed(2)} €
              </div>
            </div>
          </div>,
        );
      }
    }

    first_tax_amount = this.getTaxAmount(first_price, percentage);
    next_tax_amount = this.getTaxAmount(next_price, percentage);
    elements.push(
      <div className="form-row" key="summary-tax">
        <div className="signup-sum-item">
          <div className="signup-sum-item-label">
            {t("vat_per", { per: percentage })}
          </div>
          <div className="signup-sum-item-price">
            {first_tax_amount.toFixed(2)} €
          </div>
        </div>
      </div>,
    );

    elements.push(
      <div className="form-row" key="summary-total-title">
        <div className="signup-sum-item signup-sum-separator">
          <div className="signup-sum-total-title">{t("total")}</div>
        </div>
      </div>,
      <div className="form-row" key="summary-total-first">
        <div className="signup-sum-item signup-sum-total">
          <div className="signup-sum-total-value">
            {(first_price + first_tax_amount).toFixed(2)}€
          </div>
          <div className="signup-sum-total-label">{t("first_month")}</div>
        </div>
      </div>,
      <div className="form-row" key="summary-total-next">
        <div className="signup-sum-item signup-sum-total">
          <div className="signup-sum-total-value">
            {(next_price + next_tax_amount).toFixed(2)}€
          </div>
          <div className="signup-sum-total-label">{t("next_month")}</div>
        </div>
      </div>,
    );

    return elements;
  };

  render() {
    const { t, stripeElementOptions } = this.props;
    let validationComponent = null;

    if (!this.state.validation.isValid && this.state.showValidation)
      validationComponent = (
        <NotifMessage
          message={t(`error:${this.state.validation.error}`)}
          severity="error"
        />
      );

    return (
      <div className="signup-step-container signup-step-form-container">
        {validationComponent}
        <div className="signup-payment-container">
          <div className="signup-payment-wrapper">
            <form
              className="signup-login-form signup-payment-form"
              onSubmit={(e) => {
                e.preventDefault();
                this.onSignup();
              }}
            >
              <div className="editor-form-section section-separator">
                <div className="form-row">
                  <div className="signup-payment-panel signup-payment-billing">
                    <div className="signup-payment-title">
                      {t("billing_info")}
                    </div>
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("full_name")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              id="full_name"
                              value={this.state.full_name}
                              placeholder={t("full_name")}
                              aria-label={t("full_name")}
                              aria-describedby="full_name"
                              onChange={(e) => {
                                this.onChange(e.target);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("address")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              id="address"
                              value={this.state.address}
                              placeholder={t("address")}
                              aria-label={t("address")}
                              aria-describedby="address"
                              onChange={(e) => {
                                this.onChange(e.target);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("city")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              id="city"
                              value={this.state.city}
                              placeholder={t("city")}
                              aria-label={t("city")}
                              aria-describedby="city"
                              onChange={(e) => {
                                this.onChange(e.target);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("zip_code")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              id="zip_code"
                              value={this.state.zip_code}
                              placeholder={t("zip_code")}
                              aria-label={t("zip_code")}
                              aria-describedby="zip_code"
                              onChange={(e) => {
                                this.onChange(e.target);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("common:country")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <Select
                              name="form-country-select"
                              onChange={this.onSelectCountry}
                              options={this.getCountryOptions()}
                              value={this.state.country}
                              className="editor-select"
                              classNamePrefix="slct"
                              placeholder={t("common:country")}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="signup-payment-panel signup-payment-card">
                    <div className="signup-payment-title">{t("card_info")}</div>
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("cardholder_name")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              id="cardholder_name"
                              value={this.state.cardholder_name}
                              placeholder={t("cardholder_name")}
                              aria-label={t("cardholder_name")}
                              aria-describedby="cardholder_name"
                              onChange={(e) => {
                                this.onChange(e.target);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("card_number")}
                        </div>
                        <div className="form-group stripe-form-group">
                          <CardNumberElement
                            className="form-control"
                            options={stripeElementOptions}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("card_expiry")}
                        </div>
                        <div className="form-group stripe-form-group">
                          <CardExpiryElement
                            className="form-control"
                            options={stripeElementOptions}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("card_cvc")}
                        </div>
                        <div className="form-group stripe-form-group">
                          <CardCvcElement
                            className="form-control"
                            options={stripeElementOptions}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="editor-form-section">
                <div className="form-row">
                  <div className="signup-payment-panel signup-payment-vat">
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("btn_vat")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <div className="input-group-addon">
                              <i className="material-icons">room</i>
                            </div>
                            <Select
                              name="form-field-name"
                              id="country_vat"
                              closeMenuOnSelect
                              value={this.state.country_vat}
                              onChange={(e) => {
                                this.onChangeTax(e);
                              }}
                              options={this.getCountryRatesOptions()}
                              placeholder={t("common:country")}
                              className="editor-select editor-select-addon"
                              classNamePrefix="slct"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="signup-payment-panel signup-payment-vat">
                    <div className="form-row">
                      <div className="signup-form-item">
                        <div className="signup-form-item-title">
                          {t("vat_number")}
                        </div>
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              id="vat_number"
                              disabled={this.state.isWorld}
                              value={this.state.vat_number}
                              placeholder={t("vat_number")}
                              aria-label="vat_number"
                              aria-describedby="vat_number"
                              onChange={(e) => {
                                this.onChange(e.target);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
            <div className="signup-payment-certification">
              {t("secured_payement")}
            </div>
          </div>
          <div className="signup-payment-panel signup-payment-summary">
            <div className="signup-payment-title">{t("order_summary")}</div>
            {this.getPlanSummary()}
          </div>
        </div>
        <div className="signup-btn-container">
          <div className="signup-btn-wrapper">
            <div
              className={`signup-btn-confirm w-60 ${this.state.loading ? "signup-btn-disabled" : ""} `}
              onClick={this.state.loading ? undefined : this.onPayment}
            >
              {this.state.loading ? (
                <Loader className="animate-spin" />
              ) : (
                <span>{t("btn_confirm_step3")}</span>
              )}
            </div>
            <LangSelector languages={["en", "fr"]} />
          </div>
        </div>
      </div>
    );
  }
}

SignupStep3UI.propTypes = {
  t: PropTypes.func.isRequired,
  stripe: PropTypes.object,
  elements: PropTypes.object,
  onPayment: PropTypes.func.isRequired,
  onConfirmPayment: PropTypes.func.isRequired,
  selected_plan: PropTypes.string.isRequired,
  active_options: PropTypes.array.isRequired,
  plans: PropTypes.object.isRequired,
  plan_options: PropTypes.object.isRequired,
  countries: PropTypes.object.isRequired,
  stripeElementOptions: PropTypes.object,
};

SignupStep3UI.defaultProps = {
  stripe: null,
  elements: null,
  stripeElementOptions: STRIPE_ELEMENT_OPTIONS.light,
};

const mapStateToProps = (state) => {
  const { plans, plan_options, countries } = state.fetchReducer;
  const { stripe_key } = state.architectureReducer;

  return {
    plans,
    plan_options,
    countries,
    stripe_publishable_key: stripe_key,
  };
};

const SignupStep3 = connect(mapStateToProps)(
  withTranslation(
    "editor-signup",
    "common",
    "error",
    "languages",
  )(SignupStep3UI),
);

export function SignupStep3WithStripe(props) {
  const stripePromise = useMemo(
    () =>
      props.stripe_publishable_key
        ? loadStripe(props.stripe_publishable_key)
        : null,
    [props.stripe_publishable_key],
  );

  const { resolvedTheme } = useTheme();

  return (
    <Elements stripe={stripePromise}>
      <ElementsConsumer>
        {({ stripe, elements }) => (
          <SignupStep3
            {...props}
            stripe={stripe}
            elements={elements}
            stripeElementOptions={STRIPE_ELEMENT_OPTIONS[resolvedTheme]}
          />
        )}
      </ElementsConsumer>
    </Elements>
  );
}

SignupStep3WithStripe.propTypes = {
  stripe_publishable_key: PropTypes.string,
};

SignupStep3WithStripe.defaultProps = {
  stripe_publishable_key: null,
};

const mapStripeStateToProps = (state) => ({
  stripe_publishable_key: state.architectureReducer.stripe_key,
});

export default connect(mapStripeStateToProps)(SignupStep3WithStripe);
